import { FC, useMemo, useCallback, useState, useEffect } from "react"
import { getValue, hasValue } from "../../../../../../Models/Client/FetchState"
import { RelabelConfirmModal } from "../Common/RelabelConfirmModal"
import { CancelConfirmModal } from "../Common/CancelConfirmModal"
import { EnqueueModal } from "../Common/EnqueueModel"
import { EditTagsModal } from "../Common/EditTagsModal"
import {
    APIJobModel,
    JobStatus,
    APIJobListParams,
    getJobID,
    isCancelableJob,
    isRelabelableJob,
    isEnqueueableJob,
    isDeletableJob,
    relabelable,
    cancelable,
    enqueuable,
    deletable
} from "../../../../../../Models/API/JobModel"
import { useDispatch } from "react-redux"
import { MainThunkDispatch } from "../../../../../../Reducers"
import {
    batchAddTagsByFilter,
    batchAddTagsByJobID,
    batchRemoveTagsByJobID,
    batchRemoveTagsByFilter,
    requestSelectedJobsCancel,
    requestFilteredJobsCancel,
    requestFilteredJobsRelabel,
    enqueueSelected,
    enqueueBatch,
    requestSelectedJobsRelabel,
    deleteSelected,
    deleteBatch
} from "../../../../../../Actions/API/Job/JobActions"
import { DeleteModal } from "../Common/DeleteModal"
import { DownloadTypeModal } from "../../../Common/Modals/DownloadTypeModal/DownloadTypeModal"
import {
    DownloadType,
    useJobListDownload
} from "../../../../../../Hooks/useJobListDownload"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { APIJobsPerStatusModel } from "../../../../../../Models/API/APIJobsPerStatusModel"
import { useTagList } from "../../../../../../Hooks/useTagList"
import {
    findImageUrlPathForDownload,
    getAnnotationSchema,
    getAnnotationTitlesWithKeys,
    hasTableContent
} from "../../../../../../Helpers/Schema/Schema"
import { useAppSchema } from "../../../../../../Hooks/useAppSchema"
import {
    turnOffAppDownloadSave,
    updateAppDownloadSettings
} from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import {
    AppDownloadSettings,
    defaultAppSettings
} from "../../../../../../Models/API/AppSettings"

export type JobsBatchActionType =
    | "relabel"
    | "cancel"
    | "enqueue"
    | "add-tags"
    | "remove-tags"
    | "delete"
    | "download"

interface JobsBatchActionModalProps {
    type: JobsBatchActionType
    appID: string
    appName: string
    params: APIJobListParams
    selectedJobs: APIJobModel[]
    countByStatus: APIJobsPerStatusModel | undefined
    filteredCount: number
    onClose: () => void
    onActionDone: () => void
    isMessages?: boolean
}

type ActionTarget = number | string[]

function getCount(target: ActionTarget): number {
    return typeof target === "number" ? target : target.length
}

export const JobsBatchActionModal: FC<JobsBatchActionModalProps> = ({
    type,
    appID,
    appName,
    params,
    selectedJobs,
    countByStatus,
    filteredCount,
    onClose,
    onActionDone,
    isMessages = false
}) => {
    const instanceFetch = useAppInstance(appID)
    const templateFetch = useAppTemplate(appID)
    const appSchemaFetch = useAppSchema(appID, false)

    const [tagSearchKeyword, setTagSearchKeyword] = useState("")
    const tagsFetch = useTagList(appID, tagSearchKeyword)
    const [selected] = useState(selectedJobs)

    let initialDownloadSettings = hasValue(instanceFetch)
        ? instanceFetch.value.settings.downloadSettings
        : defaultAppSettings.downloadSettings
    const [downloadSettings, setDownloadSettings] = useState(
        initialDownloadSettings
    )

    const dispatch = useDispatch<MainThunkDispatch>()

    const isLabelByCollaborator =
        hasValue(instanceFetch) && !instanceFetch.value.canoticCrowd
    const processSelected = selected.length > 0

    const count = useMemo(
        () =>
            processSelected
                ? APIJobsPerStatusModel.aggregate(selected)
                : countByStatus,
        [selected, processSelected, countByStatus]
    )

    const targetGenerator = useCallback(
        (test: (job: APIJobModel) => boolean, statuses: () => JobStatus[]) =>
            processSelected
                ? selected.filter(test).map(getJobID)
                : statuses().reduce((total, status) => {
                      if (status === JobStatus.IN_CREATION) {
                          return total
                      }
                      return total + (count?.[status] ?? 0)
                  }, 0),
        [count, processSelected, selected]
    )

    const relabelTarget = useMemo(
        () => targetGenerator(isRelabelableJob, relabelable),
        [targetGenerator]
    )

    const cancelTarget = useMemo(
        () => targetGenerator(isCancelableJob, cancelable),
        [targetGenerator]
    )

    const enqueueTarget = useMemo(
        () => targetGenerator(isEnqueueableJob, enqueuable),
        [targetGenerator]
    )

    const deleteTarget = useMemo(
        () => targetGenerator(isDeletableJob, deletable),
        [targetGenerator]
    )

    const handleCancel = useCallback(
        async () =>
            dispatch(
                selected.length > 0
                    ? requestSelectedJobsCancel(
                          appID,
                          selected.filter(isCancelableJob).map(getJobID)
                      )
                    : requestFilteredJobsCancel(appID, {
                          onlyIfFeedback: isMessages,
                          ...params
                      })
            )
                .then(onClose)
                .then(onActionDone),
        [dispatch, selected, appID, isMessages, params, onClose, onActionDone]
    )

    const handleRelabel = useCallback(
        async () =>
            dispatch(
                selected.length > 0
                    ? requestSelectedJobsRelabel(
                          appID,
                          selected.filter(isRelabelableJob).map(getJobID)
                      )
                    : requestFilteredJobsRelabel(appID, {
                          onlyIfFeedback: isMessages,
                          ...params
                      })
            )
                .then(onClose)
                .then(onActionDone),
        [dispatch, selected, appID, params, isMessages, onClose, onActionDone]
    )

    const handleEnqueue = useCallback(
        async () =>
            dispatch(
                selected.length > 0
                    ? enqueueSelected(
                          appID,
                          selected.filter(isEnqueueableJob).map(getJobID)
                      )
                    : enqueueBatch(appID, {
                          onlyIfFeedback: isMessages,
                          ...params
                      })
            )
                .then(onClose)
                .then(onActionDone),
        [dispatch, selected, appID, params, isMessages, onClose, onActionDone]
    )

    const handleDelete = useCallback(
        async () =>
            dispatch(
                selected.length > 0
                    ? deleteSelected(
                          appID,
                          selected.filter(isDeletableJob).map(getJobID)
                      )
                    : deleteBatch(appID, {
                          onlyIfFeedback: isMessages,
                          ...params
                      })
            )
                .then(onClose)
                .then(onActionDone),
        [dispatch, selected, appID, params, isMessages, onClose, onActionDone]
    )

    const handleAddTags = useCallback(
        async (tags: string[]) => {
            await dispatch(
                selected.length === 0
                    ? batchAddTagsByFilter(
                          appID,
                          { onlyIfFeedback: isMessages, ...params },
                          tags
                      )
                    : batchAddTagsByJobID(
                          appID,
                          selected.map(job => job.id),
                          tags
                      )
            )

            onClose()
        },
        [dispatch, selected, appID, params, isMessages, onClose]
    )

    const handleRemoveTags = useCallback(
        async (tags: string[]) => {
            await dispatch(
                selected.length === 0
                    ? batchRemoveTagsByFilter(
                          appID,
                          { onlyIfFeedback: isMessages, ...params },
                          tags
                      )
                    : batchRemoveTagsByJobID(
                          appID,
                          selected.map(job => job.id),
                          tags
                      )
            )

            onClose()
        },
        [dispatch, selected, appID, params, isMessages, onClose]
    )

    const isCsvDownloadAllowed = useMemo(
        () => getValue(templateFetch)?.metadata.allow_csv_download ?? true,
        [templateFetch]
    )

    const imageDownloadPath = useMemo(
        () =>
            findImageUrlPathForDownload(getValue(templateFetch)?.outputSchema),
        [templateFetch]
    )

    const allowResponseDataDownload = useMemo(
        () =>
            selectedJobs.length > 0 &&
            (imageDownloadPath !== undefined ||
                getValue(templateFetch)?.metadata.post_process_data ===
                    "available"),
        [selectedJobs, imageDownloadPath, templateFetch]
    )

    let initialDownloadType: DownloadType = "json"
    if (isCsvDownloadAllowed !== false) {
        if (downloadSettings.expandOutputColumns) {
            initialDownloadType = "flat_csv"
        } else {
            initialDownloadType = "csv"
        }
    }
    const [downloadType, setDownloadType] =
        useState<DownloadType>(initialDownloadType)

    const hasTableAnnotations = useMemo(() => {
        return hasTableContent(getValue(appSchemaFetch)?.output.schema)
    }, [appSchemaFetch])

    const hasAnnotations = useMemo(() => {
        return !!getAnnotationSchema(getValue(appSchemaFetch)?.output.schema)
    }, [appSchemaFetch])

    const annotationTitles = useMemo(() => {
        return getAnnotationTitlesWithKeys(
            getValue(appSchemaFetch)?.output.schema
        )
    }, [appSchemaFetch])

    const isFlattenTablesEnabled =
        isCsvDownloadAllowed &&
        downloadType === "flat_csv" &&
        hasTableAnnotations

    const isIncludeAnnotationsEnabled =
        isCsvDownloadAllowed && downloadType === "flat_csv" && hasAnnotations

    const download = useJobListDownload(
        appName,
        appID,
        selected,
        params,
        downloadType,
        imageDownloadPath,
        downloadSettings,
        getValue(templateFetch)?.metadata.post_process_data === "available",
        isMessages
    )

    const handleDownload = useCallback(async () => {
        if (downloadSettings.saveSettings) {
            await dispatch(updateAppDownloadSettings(appID, downloadSettings))
        }

        if (
            !downloadSettings.saveSettings &&
            downloadSettings.saveSettings !==
                initialDownloadSettings.saveSettings
        ) {
            await dispatch(turnOffAppDownloadSave(appID))
        }

        if (download.perform) await download.perform()
    }, [
        appID,
        dispatch,
        download,
        downloadSettings,
        initialDownloadSettings.saveSettings
    ])

    useEffect(() => {
        if (download.success) onClose()
    }, [download.success, onClose])

    const handleDownloadSettingChange = useCallback(
        async (newDownloadSettings: AppDownloadSettings) => {
            setDownloadSettings(newDownloadSettings)
        },
        []
    )

    if (!hasValue(templateFetch)) {
        return null
    }

    switch (type) {
        case "download":
            return (
                <DownloadTypeModal
                    name={{ singular: "data point", plural: "data points" }}
                    itemCount={selected.length || filteredCount}
                    onClose={onClose}
                    onConfirm={download.perform ? handleDownload : undefined}
                    onSetType={setDownloadType}
                    type={downloadType}
                    willDownload={
                        download.willDownload || !downloadSettings.sendEmail
                    }
                    isMessages={isMessages}
                    defaultType={isCsvDownloadAllowed ? "flat_csv" : "json"}
                    allowCsvDownload={isCsvDownloadAllowed}
                    allowResponseDataDownload={allowResponseDataDownload}
                    downloadSettings={{
                        appDownloadSettings: downloadSettings,
                        onDownloadSettingsChanged: handleDownloadSettingChange,
                        annotationTitles,
                        isFlattenTablesEnabled,
                        isIncludeAnnotationsEnabled,
                        isSendEmailEnabled: !download.willDownload,
                        isCustomFileNamesEnabled: selected.length === 1
                    }}
                />
            )
        case "relabel":
            return (
                <RelabelConfirmModal
                    jobsByStatus={count}
                    total={getCount(relabelTarget)}
                    onYes={handleRelabel}
                    onNo={onClose}
                    isLabelByCollaborator={isLabelByCollaborator}
                />
            )
        case "cancel":
            return (
                <CancelConfirmModal
                    jobsByStatus={count}
                    total={getCount(cancelTarget)}
                    onYes={handleCancel}
                    onNo={onClose}
                    isLabelByCollaborator={isLabelByCollaborator}
                />
            )
        case "enqueue":
            return (
                <EnqueueModal
                    onNo={onClose}
                    onYes={handleEnqueue}
                    jobsByStatus={count}
                    total={getCount(enqueueTarget)}
                    isLabelByCollaborator={isLabelByCollaborator}
                />
            )
        case "delete":
            return (
                <DeleteModal
                    onNo={onClose}
                    onYes={handleDelete}
                    total={getCount(deleteTarget)}
                />
            )
        case "add-tags":
            return (
                <EditTagsModal
                    mode="add"
                    affectedJobCount={
                        selected.length > 0 ? selected.length : filteredCount
                    }
                    tagsFetch={tagsFetch}
                    onSave={handleAddTags}
                    onClose={onClose}
                    onSearchTags={setTagSearchKeyword}
                />
            )
        case "remove-tags":
            return (
                <EditTagsModal
                    mode="remove"
                    affectedJobCount={
                        selected.length > 0 ? selected.length : filteredCount
                    }
                    tagsFetch={tagsFetch}
                    onSave={handleRemoveTags}
                    onClose={onClose}
                    onSearchTags={setTagSearchKeyword}
                />
            )
    }
}
